import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, Button, Box, IconButton, Tooltip, Select, MenuItem, FormControl, InputLabel, Fade, Zoom } from '@mui/material';
import FlipIcon from '@mui/icons-material/Flip';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SpeedIcon from '@mui/icons-material/Speed';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import LanguageIcon from '@mui/icons-material/Language';
import SchoolIcon from '@mui/icons-material/School';

function Flashcards() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechRate, setSpeechRate] = useState(1);
  const [selectedLanguage, setSelectedLanguage] = useState('fr');

  const languageData = {
    fr: {
      code: 'fr-FR',
      name: 'French',
      flashcards: [
        {
          front: 'Bonjour',
          back: 'Hello',
          example: 'Bonjour, comment allez-vous?'
        },
        {
          front: 'Au revoir',
          back: 'Goodbye',
          example: 'Au revoir, à bientôt!'
        },
        {
          front: 'Merci',
          back: 'Thank you',
          example: 'Merci beaucoup!'
        }
      ]
    },
    de: {
      code: 'de-DE',
      name: 'German',
      flashcards: [
        {
          front: 'Guten Tag',
          back: 'Good day',
          example: 'Guten Tag, wie geht es Ihnen?'
        },
        {
          front: 'Auf Wiedersehen',
          back: 'Goodbye',
          example: 'Auf Wiedersehen, bis bald!'
        },
        {
          front: 'Danke',
          back: 'Thank you',
          example: 'Danke schön!'
        }
      ]
    },
    zh: {
      code: 'zh-CN',
      name: 'Mandarin Chinese',
      flashcards: [
        {
          front: '你好',
          back: 'Hello',
          example: '你好，你好吗？'
        },
        {
          front: '再见',
          back: 'Goodbye',
          example: '再见，下次见！'
        },
        {
          front: '谢谢',
          back: 'Thank you',
          example: '谢谢，非常感谢！'
        }
      ]
    },
    es: {
      code: 'es-ES',
      name: 'Spanish',
      flashcards: [
        {
          front: 'Hola',
          back: 'Hello',
          example: 'Hola, ¿cómo estás?'
        },
        {
          front: 'Adiós',
          back: 'Goodbye',
          example: 'Adiós, hasta pronto!'
        },
        {
          front: 'Gracias',
          back: 'Thank you',
          example: '¡Muchas gracias!'
        }
      ]
    },
    ru: {
      code: 'ru-RU',
      name: 'Russian',
      flashcards: [
        {
          front: 'Привет',
          back: 'Hello',
          example: 'Привет, как дела?'
        },
        {
          front: 'До свидания',
          back: 'Goodbye',
          example: 'До свидания, до скорой встречи!'
        },
        {
          front: 'Спасибо',
          back: 'Thank you',
          example: 'Большое спасибо!'
        }
      ]
    }
  };

  const handleLanguageChange = (event) => {
    const newLang = event.target.value;
    setSelectedLanguage(newLang);
    setCurrentIndex(0);
    setIsFlipped(false);
    window.speechSynthesis.cancel();
  };

  const handlePronunciation = (text, rate = speechRate) => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = languageData[selectedLanguage].code;
    utterance.rate = rate;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  const handleSpeedChange = () => {
    const newRate = speechRate === 1 ? 0.75 : speechRate === 0.75 ? 1.25 : 1;
    setSpeechRate(newRate);
    if (isSpeaking) {
      handlePronunciation(languageData[selectedLanguage].flashcards[currentIndex].front, newRate);
    }
  };

  const handleNext = () => {
    setIsFlipped(false);
    setCurrentIndex((prev) => (prev + 1) % languageData[selectedLanguage].flashcards.length);
    window.speechSynthesis.cancel();
  };

  const handlePrevious = () => {
    setIsFlipped(false);
    setCurrentIndex((prev) => (prev - 1 + languageData[selectedLanguage].flashcards.length) % languageData[selectedLanguage].flashcards.length);
    window.speechSynthesis.cancel();
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4,
          background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SchoolIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Flashcards
            </Typography>
          </Box>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Select Language</InputLabel>
            <Select
              value={selectedLanguage}
              label="Select Language"
              onChange={handleLanguageChange}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.dark',
                },
              }}
            >
              {Object.entries(languageData).map(([code, data]) => (
                <MenuItem key={code} value={code}>{data.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Fade in={true} timeout={1000}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4,
              perspective: '1000px',
              cursor: 'pointer',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.3s ease'
            }}
            onClick={handleFlip}
          >
            <Box
              sx={{
                position: 'relative',
                height: '300px',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                transformStyle: 'preserve-3d',
                transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              }}
            >
              {/* Front of card */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  borderRadius: 2,
                  color: 'white',
                  p: 3,
                }}
              >
                <Typography 
                  variant="h2" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  {languageData[selectedLanguage].flashcards[currentIndex].front}
                </Typography>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    opacity: 0.9,
                    fontStyle: 'italic',
                    mb: 2
                  }}
                >
                  [{languageData[selectedLanguage].name}]
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={isSpeaking ? "Stop" : "Pronounce"}>
                    <IconButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePronunciation(languageData[selectedLanguage].flashcards[currentIndex].front);
                      }}
                      sx={{ 
                        color: 'white',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      {isSpeaking ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={`Speed: ${speechRate}x`}>
                    <IconButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSpeedChange();
                      }}
                      sx={{ 
                        color: 'white',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      <SpeedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Back of card */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(45deg, #4CAF50 30%, #81C784 90%)',
                  borderRadius: 2,
                  color: 'white',
                  p: 3,
                }}
              >
                <Typography 
                  variant="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 'bold',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  {languageData[selectedLanguage].flashcards[currentIndex].back}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    opacity: 0.9,
                    mb: 2,
                    textAlign: 'center'
                  }}
                >
                  {languageData[selectedLanguage].flashcards[currentIndex].example}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<VolumeUpIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePronunciation(languageData[selectedLanguage].flashcards[currentIndex].example);
                  }}
                  sx={{ 
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  Pronounce Example
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<NavigateBeforeIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevious();
                }}
                sx={{ 
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.2s'
                }}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                startIcon={<FlipIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFlip();
                }}
                sx={{ 
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.2s'
                }}
              >
                Flip
              </Button>
              <Button
                variant="contained"
                endIcon={<NavigateNextIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                sx={{ 
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.2s'
                }}
              >
                Next
              </Button>
            </Box>
          </Paper>
        </Fade>
      </Paper>
    </Container>
  );
}

export default Flashcards; 